import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { lookupUserDisplayName, lookupUserOptionLines, } from '../lib/siteUsers';
function DirectoryUserIcon() {
    return (_jsxs("svg", { viewBox: "0 0 24 24", fill: "none", "aria-hidden": "true", children: [_jsx("circle", { cx: "12", cy: "8", r: "3.2", stroke: "currentColor", strokeWidth: "1.8" }), _jsx("path", { d: "M5.4 19.2c.9-3.3 3.5-5.1 6.6-5.1s5.7 1.8 6.6 5.1", stroke: "currentColor", strokeWidth: "1.8", strokeLinecap: "round" })] }));
}
function userMatchesQuery(user, query) {
    const q = query.trim().toLowerCase();
    if (!q)
        return true;
    const hay = [user.firstName, user.lastName, user.email, user.siteName, user.roleName]
        .map((part) => part?.trim() ?? '')
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
    return hay.includes(q);
}
/** Icon that opens a directory-user list. Selecting one fills person details. */
export function SiteUserPicker({ users, selectedUserId, onSelect, disabled, id, className, }) {
    const rootRef = useRef(null);
    const triggerRef = useRef(null);
    const menuRef = useRef(null);
    const searchRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [highlight, setHighlight] = useState(0);
    const [menuBox, setMenuBox] = useState(null);
    const selected = users.find((user) => user.id === selectedUserId) ?? null;
    const filtered = useMemo(() => users.filter((user) => userMatchesQuery(user, query)), [users, query]);
    useEffect(() => {
        if (!open)
            return;
        setQuery('');
        setHighlight(0);
        const focusSearch = window.setTimeout(() => searchRef.current?.focus(), 0);
        return () => window.clearTimeout(focusSearch);
    }, [open]);
    useLayoutEffect(() => {
        if (!open) {
            setMenuBox(null);
            return;
        }
        const place = () => {
            const el = triggerRef.current;
            if (!el)
                return;
            const rect = el.getBoundingClientRect();
            const width = Math.min(320, Math.max(260, window.innerWidth - 24));
            const spaceBelow = window.innerHeight - rect.bottom - 12;
            const spaceAbove = rect.top - 12;
            const openUp = spaceBelow < 200 && spaceAbove > spaceBelow;
            const maxHeight = Math.min(280, Math.max(140, openUp ? spaceAbove : spaceBelow));
            const left = Math.min(Math.max(12, rect.left), window.innerWidth - width - 12);
            setMenuBox({
                top: openUp ? rect.top - maxHeight - 4 : rect.bottom + 4,
                left,
                width,
                maxHeight,
            });
        };
        place();
        window.addEventListener('resize', place);
        window.addEventListener('scroll', place, true);
        return () => {
            window.removeEventListener('resize', place);
            window.removeEventListener('scroll', place, true);
        };
    }, [open, filtered.length]);
    useEffect(() => {
        if (!open)
            return;
        function onDoc(event) {
            const target = event.target;
            if (rootRef.current?.contains(target) || menuRef.current?.contains(target))
                return;
            setOpen(false);
        }
        function onKey(event) {
            if (event.key === 'Escape') {
                event.preventDefault();
                setOpen(false);
                triggerRef.current?.focus();
            }
        }
        document.addEventListener('mousedown', onDoc);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDoc);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);
    if (users.length === 0)
        return null;
    const triggerClass = [
        'site-user-picker-trigger',
        selected ? 'is-selected' : '',
        open ? 'is-open' : '',
    ]
        .filter(Boolean)
        .join(' ');
    function choose(user) {
        onSelect(user);
        setOpen(false);
        triggerRef.current?.focus();
    }
    function moveHighlight(delta) {
        if (filtered.length === 0)
            return;
        setHighlight((index) => (index + delta + filtered.length) % filtered.length);
    }
    return (_jsxs("div", { className: ['site-user-picker', className].filter(Boolean).join(' '), ref: rootRef, children: [_jsx("button", { id: id, ref: triggerRef, type: "button", className: triggerClass, disabled: disabled, "aria-label": selected
                    ? `Directory user: ${lookupUserDisplayName(selected)}. Change directory user`
                    : 'Pick a directory user', "aria-expanded": open, "aria-haspopup": "listbox", title: selected ? lookupUserDisplayName(selected) : 'Pick a directory user', onClick: () => setOpen((value) => !value), children: _jsx(DirectoryUserIcon, {}) }), open &&
                menuBox &&
                createPortal(_jsxs("div", { ref: menuRef, className: "site-user-picker-menu", style: {
                        top: menuBox.top,
                        left: menuBox.left,
                        width: menuBox.width,
                        maxHeight: menuBox.maxHeight,
                    }, children: [_jsx("input", { ref: searchRef, className: "site-user-picker-search", type: "search", placeholder: "Search directory\u2026", value: query, "aria-label": "Search directory users", onChange: (event) => {
                                setQuery(event.target.value);
                                setHighlight(0);
                            }, onKeyDown: (event) => {
                                if (event.key === 'ArrowDown') {
                                    event.preventDefault();
                                    moveHighlight(1);
                                }
                                else if (event.key === 'ArrowUp') {
                                    event.preventDefault();
                                    moveHighlight(-1);
                                }
                                else if (event.key === 'Enter') {
                                    event.preventDefault();
                                    const user = filtered[highlight];
                                    if (user)
                                        choose(user);
                                }
                            } }), _jsxs("ul", { className: "site-user-picker-options", role: "listbox", "aria-label": "Directory users", children: [selected && (_jsx("li", { children: _jsx("button", { type: "button", role: "option", className: "site-user-picker-option is-clear", onMouseDown: (event) => {
                                            event.preventDefault();
                                            choose(null);
                                        }, children: "Clear directory user" }) })), filtered.map((user, index) => {
                                    const lines = lookupUserOptionLines(user);
                                    const active = index === highlight;
                                    return (_jsx("li", { children: _jsxs("button", { type: "button", role: "option", "aria-selected": user.id === selectedUserId, className: [
                                                'site-user-picker-option',
                                                active ? 'is-active' : '',
                                                user.id === selectedUserId ? 'is-current' : '',
                                            ]
                                                .filter(Boolean)
                                                .join(' '), onMouseEnter: () => setHighlight(index), onMouseDown: (event) => {
                                                event.preventDefault();
                                                choose(user);
                                            }, children: [_jsx("span", { className: "site-user-picker-option-name", children: lines.name }), lines.email ? (_jsx("span", { className: "site-user-picker-option-email", children: lines.email })) : null, lines.meta ? (_jsx("span", { className: "site-user-picker-option-meta", children: lines.meta })) : null] }) }, user.id));
                                }), filtered.length === 0 && (_jsx("li", { className: "site-user-picker-empty", children: "No matching users" }))] })] }), document.body)] }));
}
